/**
 * pdf-viewer.js
 *
 * <pdf-viewer data-pdf-url="…" data-open-label="…">
 *
 * Works around the iOS / mobile Safari (and Android Chrome) limitation where a
 * PDF embedded in an <iframe> only paints its FIRST page and can't be scrolled.
 * On touch/coarse-pointer devices we hide the iframe and render every page of
 * the PDF to a stacked column of <canvas> elements using PDF.js, letting the
 * surrounding modal body scroll through the whole document.
 *
 * Desktop (fine pointer) keeps the native iframe viewer, which paginates fine.
 *
 * PDF.js is loaded lazily from a CDN the first time a viewer actually becomes
 * visible (modal opened). If it can't be loaded — offline, blocked by a strict
 * Content-Security-Policy, etc. — we fall back to a plain "open PDF" link so the
 * experience degrades gracefully instead of silently showing a single page.
 */

const PDFJS_VERSION = '4.7.76';
const PDFJS_SRC = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.min.mjs`;
const PDFJS_WORKER = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${PDFJS_VERSION}/build/pdf.worker.min.mjs`;

/** Cap rendering resolution so long samples don't exhaust mobile memory. */
const MAX_PIXEL_RATIO = 2;

let pdfjsPromise = null;

function loadPdfjs() {
  if (!pdfjsPromise) {
    pdfjsPromise = import(PDFJS_SRC).then((lib) => {
      lib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER;
      return lib;
    });
  }
  return pdfjsPromise;
}

class PdfViewer extends HTMLElement {
  #started = false;
  #url = '';
  #observer = null;

  connectedCallback() {
    this.#url = this.dataset.pdfUrl || '';
    if (!this.#url) return;

    // The inline iframe viewer only fails to paginate on touch devices; leave
    // desktop alone so we don't pull PDF.js where the native viewer works.
    if (!window.matchMedia('(pointer: coarse)').matches) return;

    this.classList.add('pdf-viewer--canvas');

    // Defer all work (and the PDF + library downloads) until the viewer is
    // actually on screen — i.e. the modal has been opened.
    this.#observer = new IntersectionObserver((entries, obs) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        obs.disconnect();
        this.#render();
      }
    });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }

  async #render() {
    if (this.#started) return;
    this.#started = true;

    // Drop the iframe so it doesn't render its single page or waste bandwidth.
    this.querySelector('iframe')?.remove();

    const pages = document.createElement('div');
    pages.className = 'pdf-viewer__pages';
    this.append(pages);

    try {
      const pdfjs = await loadPdfjs();
      const doc = await pdfjs.getDocument({ url: this.#url }).promise;
      const pixelRatio = Math.min(window.devicePixelRatio || 1, MAX_PIXEL_RATIO);
      const targetWidth = this.clientWidth || pages.clientWidth || 0;

      for (let pageNumber = 1; pageNumber <= doc.numPages; pageNumber += 1) {
        const page = await doc.getPage(pageNumber);
        const unscaled = page.getViewport({ scale: 1 });
        const scale = targetWidth ? (targetWidth / unscaled.width) * pixelRatio : pixelRatio;
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        canvas.className = 'pdf-viewer__page';
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        pages.append(canvas);

        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
      }
    } catch (error) {
      this.#renderFallback();
    }
  }

  #renderFallback() {
    this.classList.add('pdf-viewer--fallback');
    this.querySelector('.pdf-viewer__pages')?.remove();

    const link = document.createElement('a');
    link.className = 'pdf-viewer__open button';
    link.href = this.#url;
    link.target = '_blank';
    link.rel = 'noopener';
    link.textContent = this.dataset.openLabel || 'Open PDF';
    this.append(link);
  }
}

if (!customElements.get('pdf-viewer')) {
  customElements.define('pdf-viewer', PdfViewer);
}
